import { useState, useEffect, useRef } from "react";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  X, 
  Upload, 
  Image as ImageIcon, 
  Loader2, 
  Tag, 
  Star, 
  ChevronRight,
  Filter,
  Zap
} from "lucide-react";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { 
  Button, 
  Badge, 
  CardGlass, 
  SectionHeader, 
  TablePro, 
  Skeleton 
} from "@/components/ui/PremiumComponents";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import API_URL from "@/utils/api";

const API = API_URL;
const BASE_URL = API_URL.replace('/api', '');

export default function AdminMenu() {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const categoryFileInputRef = useRef(null);

  // Form states
  const [itemForm, setItemForm] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    image: "",
    active: true,
    best_seller: false,
    branch_config: [],
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    image: "",
    order: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [catRes, menuRes, branchRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/public/branches`),
      ]);
      setCategories(catRes.data);
      setMenuItems(menuRes.data);
      setBranches(branchRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar el catálogo");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file, isCategory = false) => {
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Tipo de archivo no permitido");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Máximo 5MB permitido");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      const imageUrl = `${BASE_URL}${response.data.url}`;
      if (isCategory) {
        setCategoryForm(prev => ({ ...prev, image: imageUrl }));
      } else {
        setItemForm(prev => ({ ...prev, image: imageUrl }));
      }
      toast.success("Imagen optimizada y cargada");
    } catch (error) {
      toast.error("Error en la carga de imagen");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.name || !itemForm.price || !itemForm.category_id) {
      toast.error("Completa los campos vitales");
      return;
    }

    try {
      const payload = { ...itemForm, price: parseFloat(itemForm.price) };
      if (editingItem) {
        await axios.put(`${API}/menu/${editingItem.item_id}`, payload, { withCredentials: true });
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/menu`, payload, { withCredentials: true });
        toast.success("Producto creado con éxito");
      }
      fetchData();
      setItemDialogOpen(false);
      resetItemForm();
    } catch (error) {
      toast.error("Error en la sincronización");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm("¿Seguro que quieres eliminar este producto?")) return;
    try {
      await axios.delete(`${API}/menu/${itemId}`, { withCredentials: true });
      toast.success("Producto removido");
      fetchData();
    } catch (error) {
      toast.error("No se pudo eliminar");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm("¿Seguro que quieres eliminar esta categoría? Solo se ocultará del menú público.")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { withCredentials: true });
      toast.success("Categoría eliminada");
      fetchData();
      setCategoryDialogOpen(false);
    } catch (error) {
      toast.error("Error al eliminar la categoría");
    }
  };

  const handleExportExcel = () => {
    try {
      // Create a simple CSV representation of the menu
      const headers = ["ID", "Nombre", "Precio", "Categoria_ID", "Activo", "Best_Seller"];
      const rows = menuItems.map(item => [
        item.item_id,
        item.name,
        item.price,
        item.category_id,
        item.active ? "SI" : "NO",
        item.best_seller ? "SI" : "NO"
      ]);

      const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `catalogo_conexo_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Catálogo exportado (CSV)");
    } catch (error) {
      toast.error("Error al exportar");
    }
  };

  const handleOptimizeMenu = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: 'Analizando rendimiento del menú...',
        success: 'Menú optimizado: Se han priorizado los Best Sellers y corregido metadatos.',
        error: 'Error al optimizar',
      }
    );
  };

  const fileCsvRef = useRef(null);

  const handleImportCatalog = () => {
    fileCsvRef.current?.click();
  };

  const onCsvFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    toast.loading("Procesando catálogo...");
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n");
        const headers = lines[0].split(",");
        const items = [];

        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          const values = lines[i].split(",");
          
          // Basic mapping: Name, Price, Category
          const item = {
            name: values[1] || `Item ${i}`,
            price: parseFloat(values[2]) || 0,
            category_id: values[3] || (categories[0]?.category_id || ""),
            active: values[4]?.trim().toUpperCase() === "SI",
            best_seller: values[5]?.trim().toUpperCase() === "SI",
          };
          
          if (values[0] && values[0].startsWith("item_")) {
            item.item_id = values[0];
          }
          
          items.push(item);
        }

        if (items.length === 0) {
          toast.dismiss();
          toast.error("El archivo está vacío o tiene formato incorrecto");
          return;
        }

        const response = await axios.post(`${API}/menu/bulk`, items, { withCredentials: true });
        toast.dismiss();
        toast.success(`Sincronización masiva: ${response.data.processed} platos actualizados.`);
        fetchData();
      } catch (error) {
        toast.dismiss();
        toast.error("Error al procesar el archivo CSV");
      }
    };
    reader.readAsText(file);
    e.target.value = null; // Clear input
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) {
      toast.error("Nombre de categoría requerido");
      return;
    }
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.category_id}`, categoryForm, { withCredentials: true });
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/categories`, categoryForm, { withCredentials: true });
        toast.success("Categoría creada");
      }
      fetchData();
      setCategoryDialogOpen(false);
      resetCategoryForm();
    } catch (error) {
      toast.error("Error al guardar categoría");
    }
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      description: item.description || "",
      price: item.price.toString(),
      category_id: item.category_id,
      image: item.image || "",
      active: item.active,
      best_seller: item.best_seller,
      branch_config: item.branch_config || [],
    });
    setItemDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
      image: category.image || "",
      order: category.order || 0,
    });
    setCategoryDialogOpen(true);
  };

  const resetItemForm = () => {
    setEditingItem(null);
    setItemForm({
      name: "",
      description: "",
      price: "",
      category_id: "",
      image: "",
      active: true,
      best_seller: false,
      branch_config: [],
    });
  };

  const resetCategoryForm = () => {
    setEditingCategory(null);
    setCategoryForm({ name: "", description: "", image: "", order: 0 });
  };

  const updateBranchConfig = (branchId, field, value) => {
    setItemForm(prev => {
      const currentConfig = [...(prev.branch_config || [])];
      const existingIndex = currentConfig.findIndex(c => c.branch_id === branchId);
      let newEntry;
      if (existingIndex >= 0) {
        newEntry = { ...currentConfig[existingIndex], [field]: value };
        if (field === 'price') newEntry.price = value === "" ? null : parseFloat(value);
        currentConfig[existingIndex] = newEntry;
      } else {
        newEntry = { branch_id: branchId, active: true, [field]: field === 'price' ? (value === "" ? null : parseFloat(value)) : value };
        currentConfig.push(newEntry);
      }
      return { ...prev, branch_config: currentConfig };
    });
  };

  const filteredItems = menuItems.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || item.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const renderItemRow = (item) => (
    <>
      <td className="px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="relative w-14 h-14 bg-white/5 rounded-2xl overflow-hidden border border-white/10 group-hover:border-primary/50 transition-colors">
            {item.image ? (
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-6 h-6 text-slate-700" /></div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-white text-sm">{item.name}</span>
              {item.best_seller && <Badge status="warning"><Star className="w-2.5 h-2.5 mr-1 fill-current" /> TOP</Badge>}
            </div>
            <span className="text-[10px] text-slate-500 font-mono uppercase tracking-tighter">{categories.find(c => c.category_id === item.category_id)?.name}</span>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="text-xl font-black text-white">{item.price.toFixed(2)}<small className="text-xs text-slate-500 ml-1">€</small></span>
      </td>
      <td className="px-6 py-4">
        <Badge status={item.active ? 'success' : 'danger'}>{item.active ? 'ACTIVO' : 'PAUSADO'}</Badge>
      </td>
      <td className="px-6 py-4 text-right">
        <div className="flex items-center justify-end gap-2">
           <Button variant="secondary" size="sm" className="h-9 w-9 p-0" onClick={() => openEditItem(item)}><Edit className="w-4 h-4" /></Button>
           <Button variant="danger" size="sm" className="h-9 w-9 p-0" onClick={() => handleDeleteItem(item.item_id)}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </td>
    </>
  );

  return (
    <AdminLayout>
      <div className="max-w-[1400px] mx-auto space-y-8 pb-20 animate-in fade-in duration-700">
        
        <SectionHeader 
          title="Catálogo & Menú"
          subtitle="Diseña la oferta gastronómica y gestiona precios por sucursal."
          actions={
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => { resetCategoryForm(); setCategoryDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Nueva Categoría
              </Button>
              <Button onClick={() => { resetItemForm(); setItemDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
              </Button>
            </div>
          }
        />

        {/* Categories Bar */}
        <div className="flex items-center gap-3 overflow-x-auto pb-4 scrollbar-hide">
          <button 
             onClick={() => setSelectedCategory("")}
             className={`px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all whitespace-nowrap ${!selectedCategory ? 'bg-primary border-primary text-white shadow-lg shadow-primary/20' : 'bg-white/5 border-white/10 text-slate-400 hover:border-primary/30'}`}
          >
            Todos
          </button>
          {categories.map(cat => (
            <div key={cat.category_id} className="relative group">
              <button 
                onClick={() => setSelectedCategory(cat.category_id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest border transition-all whitespace-nowrap ${selectedCategory === cat.category_id ? 'bg-primary border-primary text-white shadow-lg shadow-primary/20' : 'bg-white/5 border-white/10 text-slate-400 hover:border-primary/30'}`}
              >
                {cat.name}
              </button>
              <button 
                onClick={() => openEditCategory(cat)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20"
              >
                <Edit className="w-2.5 h-2.5 text-white" />
              </button>
            </div>
          ))}
        </div>

        {/* Global Catalog Hub */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
          
          {/* Main Table */}
          <div className="lg:col-span-3">
            <CardGlass title="Productos en Catálogo">
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input 
                  type="text"
                  placeholder="Buscar por nombre o descripción..."
                  className="w-full bg-black/40 border border-white/5 rounded-2xl pl-12 pr-4 py-4 text-white text-sm outline-none focus:ring-1 ring-primary/40 transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <TablePro 
                headers={["Producto", "Precio", "Estado", "Acciones"]}
                data={filteredItems}
                renderRow={renderItemRow}
                loading={isLoading}
              />
            </CardGlass>
          </div>

          {/* Quick Stats / Tips */}
          <div className="space-y-6">
             <CardGlass title="Análisis de Menú" className="bg-gradient-to-br from-primary/5 to-transparent">
                <div className="space-y-6 pt-4">
                   <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-500">Total ítems</span>
                      <span className="text-sm font-black text-white">{menuItems.length}</span>
                   </div>
                   <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-500">Best Sellers</span>
                      <span className="text-sm font-black text-primary">{menuItems.filter(i => i.best_seller).length}</span>
                   </div>
                   <div className="pt-4 border-t border-white/5">
                      <p className="text-[10px] text-slate-400 italic">"Los productos marcados como **Best Seller** aparecen primero en el TPV y Web."</p>
                   </div>
                   <Button 
                     variant="secondary" 
                     className="w-full h-12 uppercase tracking-tight"
                     onClick={handleOptimizeMenu}
                   >
                     <Zap className="w-4 h-4 mr-2" /> Optimizar Menú
                   </Button>
                </div>
             </CardGlass>

             <CardGlass title="Accesos Rápidos">
                <div className="space-y-3 pt-2">
                   <button 
                     onClick={handleExportExcel}
                     className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group text-left"
                   >
                      <span className="text-xs font-bold text-slate-400 group-hover:text-white">Exportar Excel</span>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                   </button>
                   <button 
                     onClick={handleImportCatalog}
                     className="w-full flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors group text-left"
                   >
                      <span className="text-xs font-bold text-slate-400 group-hover:text-white">Importar Catálogo</span>
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                   </button>
                </div>
             </CardGlass>
          </div>
        </div>
      </div>

      {/* Category Modal */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="bg-slate-950 border-white/10 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-xl font-black">{editingCategory ? "Ajustar Categoría" : "Nueva Categoría"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Etiqueta</Label>
                <Input
                  className="bg-black/50 border-white/10 rounded-xl"
                  placeholder="EJ: Entrantes, Bebidas..."
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Icono / Imagen URL</Label>
                <Input
                  className="bg-black/50 border-white/10 rounded-xl"
                  placeholder="https://..."
                  value={categoryForm.image}
                  onChange={(e) => setCategoryForm({ ...categoryForm, image: e.target.value })}
                />
              </div>
               <div className="flex gap-3">
                <Button className="flex-1 h-14 text-sm font-black uppercase tracking-tighter" onClick={handleSaveCategory}>Confirmar</Button>
                {editingCategory && (
                  <Button 
                    variant="danger" 
                    size="icon" 
                    className="h-14 w-14 group transition-all active:scale-95" 
                    onClick={() => handleDeleteCategory(editingCategory.category_id)}
                  >
                    <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
        <DialogContent className="bg-slate-950 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">{editingItem ? "Editar Producto" : "Nuevo Producto del Menú"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-500">Nombre del Plato/Item</Label>
                <Input
                  className="bg-black/50 border-white/10 py-6 text-lg font-bold"
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-500">Descripción Gastronómica</Label>
                <Textarea
                  className="bg-black/50 border-white/10 min-h-[100px]"
                  value={itemForm.description}
                  onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-slate-500">Precio Base (€)</Label>
                  <Input
                    type="number"
                    className="bg-black/50 border-white/10 text-xl font-black"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-slate-500">Categoría</Label>
                  <Select
                    value={itemForm.category_id}
                    onValueChange={(val) => setItemForm({ ...itemForm, category_id: val })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 h-11">
                       <SelectValue placeholder="Elegir..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-white">
                       {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5 space-y-4">
                <div className="flex items-center justify-between">
                   <div className="flex flex-col"><span className="text-xs font-bold text-white">Producto Visible</span><span className="text-[10px] text-slate-500">Publicar en TPV y Web</span></div>
                   <Switch checked={itemForm.active} onCheckedChange={(val) => setItemForm({ ...itemForm, active: val })} />
                </div>
                <div className="flex items-center justify-between">
                   <div className="flex flex-col"><span className="text-xs font-bold text-white">Best Seller</span><span className="text-[10px] text-slate-500">Priorizar en visualización</span></div>
                   <Switch checked={itemForm.best_seller} onCheckedChange={(val) => setItemForm({ ...itemForm, best_seller: val })} />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-500">Fotografía del Producto</Label>
                <div className="relative aspect-video rounded-3xl bg-black border-2 border-dashed border-white/10 overflow-hidden flex flex-col items-center justify-center group">
                   {itemForm.image ? (
                     <img src={itemForm.image} className="w-full h-full object-cover" />
                   ) : (
                     <div className="text-center p-6 space-y-2">
                        <Upload className="w-8 h-8 text-slate-600 mx-auto" />
                        <span className="text-[10px] text-slate-500 block uppercase font-black">Subir archivo o pegar URL</span>
                     </div>
                   )}
                   <input type="file" ref={fileInputRef} className="hidden" onChange={(e) => handleImageUpload(e.target.files[0])} />
                   <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                      <Button size="sm" onClick={() => fileInputRef.current?.click()}>Cambiar</Button>
                      {itemForm.image && <Button size="sm" variant="danger" onClick={() => setItemForm({ ...itemForm, image: "" })}>Quitar</Button>}
                   </div>
                </div>
                <Input
                  className="bg-black/40 border-white/5 text-[11px] h-9"
                  placeholder="URL Directa de imagen..."
                  value={itemForm.image}
                  onChange={(e) => setItemForm({ ...itemForm, image: e.target.value })}
                />
              </div>

              {/* Branch Settings */}
              {branches.length > 0 && (
                <div className="space-y-3 pt-2">
                   <Label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2"><Tag className="w-3 h-3" /> Precios por Sucursal</Label>
                   <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                      {branches.map(branch => {
                        const bConfig = itemForm.branch_config?.find(c => c.branch_id === branch.branch_id) || {};
                        return (
                          <div key={branch.branch_id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/5">
                             <div className="flex-1 truncate pr-4 text-xs font-bold text-slate-400">{branch.name}</div>
                             <div className="flex items-center gap-3">
                                <input 
                                  className="w-16 bg-black border border-white/10 rounded-lg p-1.5 text-xs text-white text-right font-black"
                                  placeholder={itemForm.price}
                                  value={bConfig.price || ""}
                                  onChange={(e) => updateBranchConfig(branch.branch_id, "price", e.target.value)}
                                />
                                <Switch checked={bConfig.active !== false} onCheckedChange={(val) => updateBranchConfig(branch.branch_id, "active", val)} className="scale-75" />
                             </div>
                          </div>
                        );
                      })}
                   </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-8 flex gap-4 pt-6 border-t border-white/5">
            <Button className="flex-1 h-14 text-lg font-black uppercase tracking-tighter" onClick={handleSaveItem}>Guardar Producto</Button>
            <Button variant="secondary" className="px-8 h-14" onClick={() => setItemDialogOpen(false)}>Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Hidden file input for CSV Import */}
      <input 
        type="file" 
        ref={fileCsvRef} 
        style={{ display: "none" }} 
        accept=".csv" 
        onChange={onCsvFileChange} 
      />
    </AdminLayout>
  );
}
